/*
	The `FilterHeader` component just provides a bridge between a reducer with our `common/ColumnFilters` package
	 to Rhombus's `HeaderFilter`, allowing us to configure grid columns to have filtering, by just specifying a
	 `filterSelector` function for the relevant filter configuration data, and the optional desired redux action to
	 dispatch when the filter selection changes.

	e.g. A DataGrid column configuration object might look like this:

	```
		import { Libraries } from "reducers/Libraries";
		...
		{
			key: "mediaBuyerName",
			name: "Media Buyer",
			resizable:true,
			sortable:true,
			headerRenderer: (
				<FilterHeader
					Reducer = Libraries
					filterSelector = {state => state.libraries.columnFilters.active.mediaBuyerName}
					onChangeAction = {(values) => {
						return Libraries.updateColumnFilter("mediaBuyerName", {
							params: values,
						});
					}}
				/>
			),
		}
	```

	Basic update actions are provided for free, so for simple use cases, you only need to specify
	the `Reducer` and `filterSelector`, and it will update appropriately for the selected filter's `type`.

	If you do specify an `onChangeAction`, it must be aware of the arguments provided by Rhombus's filter modal components,
	which vary depending on the `filterType` prop received by Rhombus's `FilterModalLauncher`.

	Also note that `onChangeAction` is actually supposed to be a redux action *creator*.  It takes the `onFilterChange`
	values from Rhombus's filter modals, and returns a redux action.

	See the default actions for an idea.
*/
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import { HeaderFilter as RhombusFilterHeader } from "rhombus-react";
import {
	API_DATE_FORMAT,
	TURN_OF_THE_CENTURY,
	USER_LOCALE
} from "utils/constants";
import { isActiveFilter } from "reducers/common/ColumnFilters";

import "./FilterHeader.css";

/*
	Rhombus's HeaderFilter basically has a different interface depending on what type of filter
	we're using, so we use separate components for each type here, so that the logic for it
	isn't all entangled, and we can use a minimal set of params, so it's easier to understand on this end.

	This should also help to plot out the differences when/if we attempt to improve the Rhombus interface.

	There's a lot of duplicate code here, but because this stuff is so fragile, it shouldn't be consolidated.
	Trying to make a general case component is likely to make tweaking one type break the others.

	Keeping them separate limits the potential damage of a mistake.
*/

function StringFilterHeader({ Reducer, filter, onChangeAction }){
	let dispatch = useDispatch();
	let { name, displayName, params, type } = filter;

	function defaultStringFilterChangeAction(values){
		return Reducer.updateColumnFilter(name, { params: values });
	}
	if(!onChangeAction){
		onChangeAction = defaultStringFilterChangeAction;
	}

	let headerProps = {
		name: displayName,
		filterType: type,
		filterOptions: {
			/*
				Rhombus's HeaderFilter does some processing on its `selected` prop, before forwarding it
				to FilterModalLauncher, as `filterOptions.selected`, UNLESS it's a String filter.

				If it's a String filter, `selected` has to be put in `filterOptions`, which is then
				spread and forwarded to FilterModalLauncher.  For every other type of filter, attempting
				to put `selected` in `filterOptions` will result in it being overwritten.
			*/
			selected: params, // filter string selection used for showing the selection state in the modal
		},
		selected: isActiveFilter(filter), /*
			Rhombus's HeaderFilter sets a style on the filter icon depending on a truthy `selected` value.
			Even though it doesn't forward this value to FilterModalLauncher for a String filter,
			we have to set `selected` here (i.e. in two different places).
		*/
		onFilterChange({ values }){
			dispatch(onChangeAction(values));
		},
	};

	return <RhombusFilterHeader {...headerProps} />;
}
StringFilterHeader.displayName = "StringFilterHeader";

function CheckboxFilterHeader({ Reducer, filter, onChangeAction }){
	/*
		For a Checkbox `filterType`, Rhombus's HeaderFilter expects:
			`selected` in the form of an array of strings
			`items` in the form of an array of strings, which represents the checkbox options.
	*/
	let dispatch = useDispatch();
	let { name, displayName, type, options, params } = filter;

	function defaultCheckboxFilterChangeAction(values){
		return Reducer.updateColumnFilter(name, { params: values });
	}
	if(!onChangeAction){
		onChangeAction = defaultCheckboxFilterChangeAction;
	}

	let headerProps = {
		name: displayName,
		filterType: type,

		items: options,
		selected: isActiveFilter(filter)? params : false, /*
			This is used for the selection state, and for displaying the highlighted filter icon style
			for an active filter.  Because our empty selection state for Checkbox filters is an empty array,
			we have to send a boolean when the filter isn't active (because an empty array is truthy).
		*/

		onFilterChange({ values }){
			dispatch(onChangeAction(values));
		},
	};

	return <RhombusFilterHeader {...headerProps} />;
}
CheckboxFilterHeader.displayName = "CheckboxFilterHeader";

function DateFilterHeader({ Reducer, filter, onChangeAction }){
	/*
		For a Date `filterType`, Rhombus's HeaderFilter expects:
			`selected` in the form of an object: { startDate, endDate }
	*/
	let dispatch = useDispatch();
	let { name, displayName, type, params = []} = filter;
	let [startDate, endDate] = params;
	let selected = { startDate, endDate };

	function defaultDateFilterChangeAction(values){
		let { startDate, endDate } = values; // These come back from Rhombus as Moment dates and need to be formatted for the API.
		let formattedStart = startDate?.format(API_DATE_FORMAT);
		let formattedEnd = endDate?.format(API_DATE_FORMAT);

		if(!formattedStart){ formattedStart = TURN_OF_THE_CENTURY; }
		if(!formattedEnd){
			formattedEnd = moment()
				.add(1, "day")
				.format(API_DATE_FORMAT);
		}

		return Reducer.updateColumnFilter(name, {
			params: [formattedStart, formattedEnd],
		});
	}
	if(!onChangeAction){
		onChangeAction = defaultDateFilterChangeAction;
	}

	let headerProps = {
		name: displayName,
		filterType: type,
		userLocale: USER_LOCALE,

		selected: isActiveFilter(filter)? selected : false, /*
			This is used for the selection state, and for displaying the highlighted filter icon style
			for an active filter.  Because our empty selection state for Date filters is an object,
			we have to send a boolean when the filter isn't active (because an object is truthy).
		*/

		onFilterChange({ values }){
			dispatch(onChangeAction(values));
		},
	};

	return <RhombusFilterHeader {...headerProps} />;
}
DateFilterHeader.displayName = "DateFilterHeader";

function RangeFilterHeader({ Reducer, filter, onChangeAction }){
	/*
		For a Range `filterType`, Rhombus's HeaderFilter expects:
			`selected` as a 2 element array: [lowBound, highBound]
			`items` in the form of an object: { min, max } - which represents the bounds for possible choices.

		If `items` isn't in the right format, it won't even display the filter icon, but it will fail silently.

		If the `name` contains "file size" (case insensitive), the modal will display units, up to terabytes (TB)
	*/
	let dispatch = useDispatch();
	let { name, displayName, type, options = [], params = []} = filter;

	function defaultRangeFilterChangeAction(values){
		return Reducer.updateColumnFilter(name, { params: values });
	}
	if(!onChangeAction){
		onChangeAction = defaultRangeFilterChangeAction;
	}

	let [lowBound, highBound] = params;

	let min = Math.floor(Math.min(...options));
	let max = Math.ceil(Math.max(...options));

	if(min === Infinity || max === -Infinity){
		/*
			This will happen if options is empty, in which case, our range modal isn't equipped to handle it.
			Rhombus's HeaderFilter doesn't seem to have an option to just hide the filter icon, so
			we just return the fallback.
		*/
		return <FilterHeaderFallback name={displayName} />;
	}

	let selected = [
		// Default to a full range selection
		lowBound ? lowBound : min,
		highBound ? highBound : max,
	];

	let headerProps = {
		name: displayName,
		filterType: type,

		items: { min, max },
		selected: isActiveFilter(filter)? selected: false, /*
			This is used for the selection state, and for displaying the highlighted filter icon style
			for an active filter.  Because our empty selection state for Range filters is an array,
			we have to send a boolean when the filter isn't active (because an array is truthy).
		*/

		onFilterChange({ values }){
			dispatch(onChangeAction(values));
		},
	};

	return <RhombusFilterHeader {...headerProps} />;
}
RangeFilterHeader.displayName = "RangeFilterHeader";

const FILTER_HEADER_TYPES = {
	String: StringFilterHeader,
	Checkbox: CheckboxFilterHeader,
	Date: DateFilterHeader,
	Range: RangeFilterHeader,
};

function FilterHeaderFallback({ name }){
	/*
		There may be cases where we don't want to filter, and Rhombus's HeaderFilter doesn't seem to
		provide a way to just hide the filter icon, so we define this component as a fallback.

		It's just a div with a string in it, but we may need to add classes to it or something later.
	*/
	return <div>{name}</div>;
}

class FilterHeaderErrorBoundary extends React.Component{
	/*
		When filtering errors, we don't want to crash the whole UI.

		We can just show a regular header until there's a prop change or something.
	*/
	static getDerivedStateFromError(error) {
		// Update state so the next render will show the fallback UI.
		return {
			hasError: true,
		};
	}

	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
		};
	}

	componentDidCatch(error, errorInfo) {
		console.error("Caught by FilterHeaderErrorBoundary:", error, errorInfo);
	}

	render() {
		let { name } = this.props;
		if (this.state.hasError) {
			return <FilterHeaderFallback name={name} />;
		}

		return this.props.children;
	}
}

export default function FilterHeader({
	Reducer,
	filterSelector,
	onChangeAction,
}){
	let filter = useSelector(filterSelector) || {};
	let { displayName, type = "String" } = filter;

	let HeaderComponent = FILTER_HEADER_TYPES[type];

	let headerProps = {
		Reducer,
		filter,
		onChangeAction,
	};

	return (
		<FilterHeaderErrorBoundary name={displayName}>
			<HeaderComponent {...headerProps} />
		</FilterHeaderErrorBoundary>
	);
}

FilterHeader.displayName = "FilterHeader";
